import { useMutation, useQueryClient } from "@tanstack/react-query"
import type {
	ProjectMembersResponse,
	ProjectResponse,
	ProjectStatus,
	UpdateProjectBody,
} from "@zplanem/responses"
import { webPaths } from "@zplanem/web-paths"
import { useEffect, useState } from "react"
import { Link, Navigate, Outlet, useParams } from "react-router-dom"
import { PropertyChat } from "../../components/chat/property-chat"
import { SharedChat } from "../../components/chat/shared-chat"
import { ApiError, apiFetch } from "../../lib/api-client"
import {
	OPEN_CHAT_RAIL_EVENT,
	type OpenChatRailDetail,
	readProjectChatRailOpen,
	writeProjectChatRailOpen,
} from "../../lib/chat-rail"
import { queryKeys } from "../../lib/query-keys"
import { AddProjectMemberModal } from "../components/add-project-member-modal"
import { PageActions } from "../components/page-actions"
import { PageActionsBar } from "../components/page-actions-bar"
import { PageContainer } from "../components/page-container"
import { ProjectBottomNav } from "../components/project-bottom-nav"
import { PropertyTopBar } from "../components/property-top-bar"
import { ShareModal } from "../components/share-modal"
import { useJournalCacheInvalidator } from "../hooks/use-journal-cache-invalidator"
import { useProject } from "../hooks/use-project"
import { useProjectChatChannels } from "../hooks/use-project-chat-channels"
import { useProperty } from "../hooks/use-property"
import { usePropertyMemberships } from "../hooks/use-property-memberships"
import { useStickyNonZero } from "../hooks/use-sticky-nonzero"
import { getTranslations } from "../i18n"
import { useLocale } from "../locale-context"
import { useUser } from "../user-context"
import type { ProjectDetailContext } from "./project/project-detail-context"

/**
 * Project shell (ZPL-126): owns the project's data, permissions, mutations,
 * chat rail and modals, and renders the active sub-tab through `<Outlet>`.
 * Section navigation lives in the desktop rail (ZPL-352) and the mobile
 * ProjectBottomNav; the desktop header strip above the outlet carries the
 * property-name upward link and the PageActions cluster (ZPL-353). The
 * project header (identity + Edit/Archive) belongs to the Details tab only —
 * see `ProjectHeader`. The Details tab consumes everything via
 * `ProjectDetailContext`; the Visualizations and Inspirations tabs are
 * self-contained empty states.
 */
export function ProjectDetailLayout() {
	const { id, projectId } = useParams<{ id: string; projectId: string }>()
	const { data: property, isLoading, error } = useProperty(id)
	const project = useProject(id, projectId)
	const memberships = usePropertyMemberships(property?.id)
	const { profile } = useUser()
	const { locale } = useLocale()
	const t = getTranslations(locale)
	const queryClient = useQueryClient()
	const invalidateJournal = useJournalCacheInvalidator(id)
	const [shareOpen, setShareOpen] = useState(false)
	const [actionError, setActionError] = useState<string | null>(null)
	const [addMemberOpen, setAddMemberOpen] = useState(false)
	const [editing, setEditing] = useState(false)

	const [chatRailOpen, setChatRailOpen] = useState<boolean>(() =>
		readProjectChatRailOpen(projectId),
	)
	useEffect(() => {
		// Reset state from localStorage when navigating between projects so
		// each one remembers its own open/closed preference.
		setChatRailOpen(readProjectChatRailOpen(projectId))
	}, [projectId])
	useEffect(() => {
		if (!projectId) return
		writeProjectChatRailOpen(projectId, chatRailOpen)
	}, [projectId, chatRailOpen])
	// Open the rail when a mention toast for THIS project is clicked while
	// already on this page (navigation to the current route is a no-op).
	useEffect(() => {
		if (!projectId) return
		const handler = (e: Event) => {
			const detail = (e as CustomEvent<OpenChatRailDetail>).detail
			if (detail.projectId === projectId) setChatRailOpen(true)
		}
		window.addEventListener(OPEN_CHAT_RAIL_EVENT, handler)
		return () => window.removeEventListener(OPEN_CHAT_RAIL_EVENT, handler)
	}, [projectId])
	useProjectChatChannels(id, projectId, profile?.id)
	// Computed from the query data BEFORE the early returns so this hook runs
	// unconditionally (rules-of-hooks); the plain `sharedChat` derivation lives
	// after the guards.
	const chatUnreadCount = useStickyNonZero(
		project.data?.chats?.find((c) => c.kind === "shared")?.unreadMessageCount ??
			0,
		5000,
	)

	const updateStatus = useMutation({
		mutationFn: (status: ProjectStatus) =>
			apiFetch<ProjectResponse>(
				`/api/common/properties/${id}/projects/${projectId}`,
				{ method: "PATCH", body: JSON.stringify({ status }) },
			),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: queryKeys.projects(id ?? ""),
			})
			invalidateJournal()
		},
	})

	const setArchived = useMutation({
		mutationFn: (archived: boolean) =>
			apiFetch<ProjectResponse>(
				`/api/common/properties/${id}/projects/${projectId}/${archived ? "archive" : "unarchive"}`,
				{ method: "POST", body: JSON.stringify({}) },
			),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: queryKeys.projects(id ?? ""),
			})
			invalidateJournal()
		},
	})

	const updateFields = useMutation({
		mutationFn: (body: UpdateProjectBody) =>
			apiFetch<ProjectResponse>(
				`/api/common/properties/${id}/projects/${projectId}`,
				{ method: "PATCH", body: JSON.stringify(body) },
			),
		onSuccess: () =>
			queryClient.invalidateQueries({
				queryKey: queryKeys.projects(id ?? ""),
			}),
	})

	const removeMember = useMutation({
		mutationFn: (userId: string) =>
			apiFetch<ProjectMembersResponse>(
				`/api/common/properties/${id}/projects/${projectId}/members/${userId}`,
				{ method: "DELETE" },
			),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: queryKeys.projects(id ?? ""),
			})
			invalidateJournal()
		},
	})

	const isNotFound =
		!id ||
		!projectId ||
		(error instanceof ApiError && error.status === 404) ||
		(project.error instanceof ApiError && project.error.status === 404)
	if (isNotFound) return <Navigate to={webPaths.dashboard()} replace />
	if (isLoading || project.isLoading || !property || !project.data) {
		const loadError = error ?? project.error
		return (
			<div className="min-h-screen w-full bg-background flex items-center justify-center text-muted-foreground">
				{loadError ? (
					<span className="text-destructive px-6 text-center">
						{loadError.message}
					</span>
				) : (
					t.common.loading
				)}
			</div>
		)
	}

	const p = project.data
	const sharedChat = p.chats?.find((c) => c.kind === "shared")
	const isOwner =
		memberships.data?.owner.id !== undefined &&
		memberships.data.owner.id === profile?.id
	const isArchitectMember = Boolean(
		memberships.data?.memberships.some(
			(m) => m.user.id === profile?.id && m.role === "architect",
		),
	)
	const isProjectMember = Boolean(p.members?.some((m) => m.id === profile?.id))
	const canSetStatus = isOwner || isProjectMember
	const canArchive = isOwner || isProjectMember
	const canEdit = isOwner || (isArchitectMember && isProjectMember)
	const canManageMembers = isOwner || (isArchitectMember && isProjectMember)
	const archived = p.archivedAt !== null
	const paid = p.paidAt !== null
	const canProposeCost =
		!paid && (isOwner || (isArchitectMember && isProjectMember))
	const canMarkPaid = canProposeCost

	async function run(action: Promise<unknown>) {
		setActionError(null)
		try {
			await action
		} catch (err) {
			setActionError(
				err instanceof ApiError ? err.message : t.projects.form.errors.unknown,
			)
		}
	}

	const outletContext: ProjectDetailContext = {
		propertyId: property.id,
		project: p,
		isOwner,
		canEdit,
		canSetStatus,
		canManageMembers,
		canProposeCost,
		canMarkPaid,
		canArchive,
		archived,
		editing,
		setEditing,
		onUpdateStatus: (status) => updateStatus.mutateAsync(status),
		updateStatusPending: updateStatus.isPending,
		onSetArchived: (next) => setArchived.mutateAsync(next),
		setArchivedPending: setArchived.isPending,
		onUpdateFields: (body) => updateFields.mutateAsync(body),
		updateFieldsPending: updateFields.isPending,
		onRemoveMember: (userId) => removeMember.mutateAsync(userId),
		removeMemberPending: removeMember.isPending,
		onOpenAddMember: () => setAddMemberOpen(true),
		runAction: run,
	}

	return (
		<div className="min-h-screen w-full bg-background flex flex-col">
			<PropertyTopBar
				property={property}
				project={{ id: p.id, name: p.name }}
				onShareClick={() => setShareOpen(true)}
				chatRailOpen={chatRailOpen}
				chatUnreadCount={chatUnreadCount}
				chatToggleLabel={t.chat.shared.projectHeaderName}
				onChatRailToggle={
					sharedChat ? () => setChatRailOpen((x) => !x) : undefined
				}
			/>
			<PageContainer chatRailOpen={chatRailOpen}>
				{/* The contextual actions pin to the viewport top-right (aligned
				    with the rail logo row); the property-name eyebrow stays in
				    flow as the upward path. Mobile keeps both in the fixed bar. */}
				<PageActionsBar chatRailOpen={chatRailOpen}>
					<PageActions
						className="shrink-0"
						property={property}
						onShareClick={() => setShareOpen(true)}
						chatRailOpen={chatRailOpen}
						chatUnreadCount={chatUnreadCount}
						chatToggleLabel={t.chat.shared.projectHeaderName}
						onChatRailToggle={
							sharedChat ? () => setChatRailOpen((x) => !x) : undefined
						}
					/>
				</PageActionsBar>
				<div className="mb-4 hidden md:flex">
					<Link
						to={webPaths.property(property.id)}
						className="min-w-0 truncate text-[11px] font-semibold uppercase tracking-[0.15em] text-muted-foreground hover:underline hover:text-foreground transition-colors"
					>
						{property.name}
					</Link>
				</div>
				{actionError ? (
					<div
						role="alert"
						className="mt-4 rounded-xl border border-destructive/30 bg-destructive/10 text-destructive text-sm px-4 py-3"
					>
						{actionError}
					</div>
				) : null}

				<div className="mt-5">
					<Outlet context={outletContext} />
				</div>
			</PageContainer>

			{shareOpen ? (
				<ShareModal
					propertyId={property.id}
					propertyName={property.name}
					onClose={() => setShareOpen(false)}
				/>
			) : null}
			{addMemberOpen ? (
				<AddProjectMemberModal
					propertyId={property.id}
					project={p}
					onClose={() => setAddMemberOpen(false)}
				/>
			) : null}
			{sharedChat ? (
				<SharedChat
					propertyId={property.id}
					projectId={p.id}
					members={p.members}
					chatId={sharedChat.id}
					isOpen={chatRailOpen}
					onClose={() => setChatRailOpen(false)}
				/>
			) : null}
			{/* Mareczek floating chat — present on every property sub page
			    (ZPL-100); hidden while the project chat rail is open, same
			    rail behavior as the overview page. */}
			<PropertyChat
				propertyId={property.id}
				hidden={chatRailOpen}
				contextProjectId={projectId}
			/>
			<ProjectBottomNav propertyId={property.id} projectId={p.id} />
		</div>
	)
}
